
import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Button,
    LinearProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import Close from '@mui/icons-material/Close';
import CloudUpload from '@mui/icons-material/CloudUpload';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { axiosInstance, localAxios } from '../../../Axios/axios';
import axios from 'axios';

const AddEmployeeUsingCSV = ({ open, onClose, onEmployeesAdded, organizationId }) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const fileInputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [statusMessage, setStatusMessage] = useState('');
    const [uploadStatus, setUploadStatus] = useState('idle'); 

   
    useEffect(() => {
        if (!open) { 
            handleResetForm();
        }
    }, [open]); 
    

    const handleResetForm = () => {
        setSelectedFile(null);
        setUploading(false);
        setUploadProgress(0);
        setStatusMessage('');
        setUploadStatus('idle'); 
        if (fileInputRef.current) {
            fileInputRef.current.value = ''; 
        }
    };

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
                setSelectedFile(file);
                console.log('file', file);
                setUploadStatus('idle'); 
                setStatusMessage('');
            } else {
                setSelectedFile(null);
                setStatusMessage('Please select a valid CSV file (.csv).');
                setUploadStatus('error');
            }
        }
      
        if(fileInputRef.current){
            fileInputRef.current.value = '';
        }
    };

    const handleUploadClick = () => {
        fileInputRef.current.click();
    };

    const handleRemoveFile = () => {
        setSelectedFile(null);
        setUploadProgress(0);
        setUploading(false);
        setUploadStatus('idle'); 
        setStatusMessage('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleAddEmployees = async () => {
        if (!selectedFile) {
            setStatusMessage('Please select a CSV file to upload.');
            setUploadStatus('error');
            return;
        }

        setUploading(true);
        setUploadProgress(0);
        setUploadStatus('idle'); 
        setStatusMessage('');

        const formData = new FormData();
        formData.append('organization_id', organizationId);
        formData.append('file', selectedFile);

        try {
           
            const baseConfig = localAxios('/organization/userSignupByCsv', formData, 'multipart/form-data');

            const response = await axiosInstance({
                ...baseConfig,
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setUploadProgress(percentCompleted);
                }
            });

            if (response.status === 200 || response.status === 201) {
                setUploadStatus('success');
                setStatusMessage(`"${selectedFile.name}" uploaded successfully.`);

               
                if (onEmployeesAdded) {
                    setTimeout(() => {
                        onEmployeesAdded(); 
                    }, 1000);
                }

               
                setTimeout(() => {
                    onClose(); 
                }, 2000);

            } else {
                setUploadStatus('error');
                setStatusMessage(`Upload of "${selectedFile.name}" failed: Unexpected status ${response.status}.`);
            }
        } catch (error) {
            setUploadStatus('error');
            if (error.response) {
                setStatusMessage(
                    `Upload failed for "${selectedFile.name}": ${error.response.data.message || 'Server error'}`
                );
                console.error('Upload error response:', error.response.data);
            } else if (error.request) {
                setStatusMessage(`Upload failed for "${selectedFile.name}": No response from server.`);
                console.error('Upload error request:', error.request);
            } else {
                setStatusMessage(`Upload failed for "${selectedFile.name}": ${error.message}`);
                console.error('Upload error message:', error.message);
            }
        } finally {
           
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose} 
            maxWidth="md"
            fullWidth 
        >
            <DialogTitle sx={{ backgroundColor: "#1565c0", color: "#fff", display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant='h6' fontWeight='600'>
                    Add Employee From CSV
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{ color: '#fff' }}
                >
                    <Close />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2, mt: 2 }}>
                    Upload a CSV file containing employee data. Each row should represent an employee.
                    <br />
                    <a href="/path/to/your/sample_csv_template.csv" download style={{ textDecoration: 'none', color: '#1565c0' }}>
                        Download sample CSV template
                    </a> (replace with actual path)
                </Typography>
                <Box
                    sx={{
                        border: '2px dashed #ccc',
                        borderRadius: '8px',
                        p: '24px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        '&:hover': {
                            borderColor: '#999',
                        },
                        position: 'relative'
                    }}
                    onClick={handleUploadClick}
                >
                    <input
                        type='file'
                        accept=".csv"
                        ref={fileInputRef}
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                    />
                    {!selectedFile ? (
                        <Box>
                            <CloudUpload sx={{ fontSize: 40, color: '#999' }} />
                            <Typography variant='body1' color='textSecondary'>
                                Drag and drop your CSV file here, or click to browse
                            </Typography>
                            <Button variant="outlined" sx={{ mt: 2 }}>
                                Browse Files
                            </Button>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography variant='subtitle1' sx={{ fontWeight: 'bold' }}>
                                Selected File: {selectedFile.name}
                            </Typography>
                            <Typography variant='body2' color='textSecondary' sx={{ ml: 1 }}>
                                ({(selectedFile.size / 1024).toFixed(2)} KB)
                            </Typography>
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation(); 
                                    handleRemoveFile();
                                }}
                                sx={{ ml: 1 }}
                            >
                                <Close fontSize="small" />
                            </IconButton>
                        </Box>
                    )}
                </Box>
                {uploading && (
                    <Box sx={{ width: '100%', mt: 2 }}>
                        <LinearProgress variant='determinate' value={uploadProgress} />
                        <Typography variant='body2' color='textSecondary' sx={{ mt: 1 }}>
                            Uploading: {uploadProgress}%
                        </Typography>
                    </Box>
                )}
                {statusMessage && (
                    <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        {uploadStatus === 'success' && <CheckCircleOutlineIcon color='success' />}
                        {uploadStatus === 'error' && <ErrorOutlineIcon color="error" />}
                        <Typography variant='body2'
                            color={uploadStatus === 'success' ? 'success.main' : 'error.main'}
                        >
                            {statusMessage}
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2, borderTop: '1px solid #eee' }}>
                <Button onClick={onClose} disabled={uploading}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleAddEmployees}
                    disabled={!selectedFile || uploading}
                >
                    {uploading ? 'Uploading...' : 'Add Employees'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};


export default AddEmployeeUsingCSV;